import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  uploadFile,
  deleteFile,
  getUploadedFiles,
} from '../../api/fileUploadService';

export const uploadFileThunk = createAsyncThunk(
  'fileUpload/uploadFile',
  async ({ file, folder = 'documents' }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      const response = await uploadFile(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to upload file');
    }
  }
);

export const deleteFileThunk = createAsyncThunk(
  'fileUpload/deleteFile',
  async ({ fileId, filePath }, { rejectWithValue }) => {
    try {
      await deleteFile(fileId, filePath);
      return fileId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete file');
    }
  }
);

export const getUploadedFilesThunk = createAsyncThunk(
  'fileUpload/getUploadedFiles',
  async ({ companyId }, { rejectWithValue }) => {
    try {
      const response = await getUploadedFiles(companyId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch files');
    }
  }
);

const initialState = {
  files: [],
  uploadedFiles: [],
  currentUpload: {
    fileId: null,
    fileName: '',
    progress: 0,
    isUploading: false,
  },
  loading: false,
  error: null,
  success: false,
  successMessage: '',
};

const fileUploadSlice = createSlice({
  name: 'fileUpload',
  initialState,
  reducers: {
    setUploadProgress: (state, action) => {
      state.currentUpload.progress = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.successMessage = '';
    },
    resetUpload: (state) => {
      state.currentUpload = initialState.currentUpload;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(uploadFileThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.currentUpload.isUploading = true;
    });
    builder.addCase(uploadFileThunk.fulfilled, (state, action) => {
      state.loading = false;
      state.currentUpload.isUploading = false;
      state.currentUpload.progress = 100;
      state.success = true;
      state.successMessage = 'File uploaded successfully';
      state.uploadedFiles.push(action.payload);
      setTimeout(() => {
        state.currentUpload = initialState.currentUpload;
      }, 2000);
    });
    builder.addCase(uploadFileThunk.rejected, (state, action) => {
      state.loading = false;
      state.currentUpload.isUploading = false;
      state.error = action.payload;
    });

    builder.addCase(deleteFileThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteFileThunk.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.successMessage = 'File deleted successfully';
      state.uploadedFiles = state.uploadedFiles.filter(
        (file) => file.id !== action.payload
      );
    });
    builder.addCase(deleteFileThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(getUploadedFilesThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getUploadedFilesThunk.fulfilled, (state, action) => {
      state.loading = false;
      state.uploadedFiles = action.payload;
    });
    builder.addCase(getUploadedFilesThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export const { setUploadProgress, clearError, clearSuccess, resetUpload } =
  fileUploadSlice.actions;

export default fileUploadSlice.reducer;
